import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { Bar, BarChart, CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { api } from "../services/api";
import { useUi } from "../stores/ui";
import { can, effectiveRequirementStatus, type RosterMember } from "../types";
import { Button, Card, EmptyState, PageHeader, Skeleton, StatusPill, TeamBadge, Textarea } from "../components/ui";
import { AnimatedNumber, ProgressBar } from "../components/feedback";
import { useState } from "react";

export function ProfilePage() {
  const { discordId = "" } = useParams();
  const user = useUi((s) => s.user);
  const queryClient = useQueryClient();
  const [note, setNote] = useState("");
  const { data, isLoading } = useQuery({
    queryKey: ["profile", discordId],
    queryFn: () =>
      api<{
        member: RosterMember;
        status: { progress: number };
        trend: { direction: string; percent: number };
        snapshots: { month: number; year: number; interviews: number; trainings: number; calculatedStatus: string }[];
        recent: { _id: string; type: string; quantity: number; occurredAt: string; notes: string }[];
        notes: { _id: string; body: string; authorDiscordId: string; createdAt: string }[];
        audit: { _id: string; action: string; timestamp: string }[];
        milestones: { label: string }[];
      }>(`/api/roster/${discordId}`),
  });
  const refresh = useMutation({
    mutationFn: () => api(`/api/admin/members/${discordId}/refresh`, { method: "POST" }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["profile", discordId] }),
    meta: { success: "Member refreshed from Discord" },
  });
  const addNote = useMutation({
    mutationFn: () => api(`/api/admin/notes/${discordId}`, { method: "POST", body: JSON.stringify({ body: note }) }),
    onSuccess: () => {
      setNote("");
      void queryClient.invalidateQueries({ queryKey: ["profile", discordId] });
    },
    meta: { success: "Note saved" },
  });

  if (isLoading || !data) return <Skeleton className="h-96" />;
  const member = data.member;
  const trendLabel =
    data.trend.direction === "UP"
      ? `↑ ${data.trend.percent}% from last month`
      : data.trend.direction === "DOWN"
        ? `↓ ${Math.abs(data.trend.percent)}% from last month`
        : "Stable activity versus last month";
  const chart = data.snapshots
    .slice()
    .reverse()
    .map((item) => ({
      label: `${item.month}/${item.year}`,
      interviews: item.interviews,
      trainings: item.trainings,
      total: item.interviews + item.trainings,
    }));

  return (
    <div>
      <PageHeader
        title={member.displayName}
        subtitle={`@${member.username}`}
        actions={
          can(user, "MANAGE_MEMBERS") ? (
            <Button variant="outline" className="w-full sm:w-auto" onClick={() => refresh.mutate()}>Refresh from Discord</Button>
          ) : null
        }
      />
      <div className="grid gap-4 lg:grid-cols-[280px_1fr]">
        <Card className="text-center">
          <img src={member.avatarUrl} alt="" className="mx-auto h-24 w-24 rounded-full" />
          <h2 className="mt-3 text-xl font-semibold">{member.displayName}</h2>
          <p className="text-sm text-zinc-500">@{member.username}</p>
          {can(user, "VIEW_MEMBER_IDS") ? <p className="mt-1 text-xs text-zinc-600">{member.discordId}</p> : null}
          <div className="mt-3 flex flex-wrap justify-center gap-2">
            {member.teams.length
              ? member.teams.map((team) => <TeamBadge key={team.id} team={team} />)
              : <span className="text-sm text-zinc-500">Not in team</span>}
          </div>
          <div className="mt-3"><StatusPill status={effectiveRequirementStatus(member)} /></div>
          <p className="mt-3 text-sm text-zinc-400">Joined HR {member.joinedHrAt ? new Date(member.joinedHrAt).toLocaleDateString() : "unknown"}</p>
          <p className="mt-2 text-sm text-accent">{trendLabel}</p>
        </Card>
        <div className="grid gap-4 md:grid-cols-2">
          <Card><p className="text-sm text-zinc-400">All-time interviews</p><p className="text-2xl font-semibold sm:text-3xl"><AnimatedNumber value={member.allTimeInterviews} /></p></Card>
          <Card><p className="text-sm text-zinc-400">All-time trainings</p><p className="text-2xl font-semibold sm:text-3xl"><AnimatedNumber value={member.allTimeTrainings} /></p></Card>
          <Card><p className="text-sm text-zinc-400">This month interviews</p><p className="text-3xl font-semibold">{member.monthInterviews}</p></Card>
          <Card><p className="text-sm text-zinc-400">This month trainings</p><p className="text-3xl font-semibold">{member.monthTrainings}</p></Card>
        </div>
      </div>
      <Card className="mt-4">
        <p className="mb-2 text-sm text-zinc-400">{Math.round(member.requirementProgress)}% Monthly Requirement</p>
        <ProgressBar value={member.requirementProgress} />
        {data.milestones.length ? (
          <div className="mt-3 flex flex-wrap gap-2">
            {data.milestones.map((item) => <span key={item.label} className="rounded-full bg-accent/20 px-3 py-1 text-xs">{item.label}</span>)}
          </div>
        ) : null}
      </Card>
      <div className="mt-4 grid gap-4 xl:grid-cols-2">
        <Card>
          <h3 className="mb-3 font-semibold">Activity trend</h3>
          {chart.length ? (
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={chart}>
                <CartesianGrid stroke="rgba(255,255,255,0.06)" />
                <XAxis dataKey="label" stroke="#a1a1aa" />
                <YAxis stroke="#a1a1aa" />
                <Tooltip />
                <Line dataKey="interviews" stroke="#EC4899" />
                <Line dataKey="trainings" stroke="#38BDF8" />
              </LineChart>
            </ResponsiveContainer>
          ) : <EmptyState title="No historical months yet." />}
        </Card>
        <Card>
          <h3 className="mb-3 font-semibold">Total activity</h3>
          {chart.length ? (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={chart}>
                <CartesianGrid stroke="rgba(255,255,255,0.06)" />
                <XAxis dataKey="label" stroke="#a1a1aa" />
                <YAxis stroke="#a1a1aa" />
                <Tooltip />
                <Bar dataKey="total" fill="#EC4899" radius={8} />
              </BarChart>
            </ResponsiveContainer>
          ) : <EmptyState title="No historical months yet." />}
        </Card>
      </div>
      <Card className="mt-4">
        <h3 className="mb-3 font-semibold">Recent logs</h3>
        {data.recent.length ? data.recent.map((item) => (
          <div key={item._id} className="flex flex-col gap-1 border-t border-white/5 py-2 text-sm sm:flex-row sm:justify-between">
            <span>{item.quantity} {item.type.toLowerCase()} — {item.notes || "No notes"}</span>
            <span className="text-zinc-500">{new Date(item.occurredAt).toLocaleString()}</span>
          </div>
        )) : <EmptyState title="No HR activity has been logged yet." />}
      </Card>
      {can(user, "MANAGE_NOTES") ? (
        <Card className="mt-4">
          <h3 className="mb-3 font-semibold">Manager notes</h3>
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Private leadership note" />
          <Button className="mt-3" onClick={() => addNote.mutate()} disabled={!note.trim()}>Save note</Button>
          <div className="mt-4 space-y-2 text-sm">
            {data.notes.map((item) => (
              <div key={item._id} className="rounded-xl bg-white/5 p-3">
                <p>{item.body}</p>
                <p className="mt-1 text-xs text-zinc-500">{new Date(item.createdAt).toLocaleString()}</p>
              </div>
            ))}
          </div>
        </Card>
      ) : null}
      {can(user, "VIEW_AUDIT_LOGS") ? (
        <Card className="mt-4">
          <h3 className="mb-3 font-semibold">Audit history</h3>
          {data.audit.map((item) => (
            <div key={item._id} className="flex flex-col gap-1 border-t border-white/5 py-2 text-sm sm:flex-row sm:justify-between">
              <span>{item.action}</span>
              <span className="text-zinc-500">{new Date(item.timestamp).toLocaleString()}</span>
            </div>
          ))}
        </Card>
      ) : null}
    </div>
  );
}
